<template>
    <div class="lamp-form-container">
        <el-card class="form-card" shadow="hover">
            <template #header>
                <div class="form-header">
                    <h2>路灯信息提交</h2>
                    <el-divider />
                </div>
            </template>

            <el-form ref="lampForm" :model="formData" :rules="formRules" label-width="120px" label-position="top"
                class="lamp-form">
                <!-- 路灯直播日期 -->
                <el-form-item label="路灯直播日期" prop="lamptime">
                    <el-date-picker v-model="formData.lamptime" type="date" placeholder="选择路灯直播日期"
                        value-format="YYYY-MM-DD" class="date-picker" :picker-options="pickerOptions" />
                    <div class="form-tip">请选择路灯直播的具体日期</div>
                </el-form-item>

                <!-- 路灯时间段 - 改为选择器 -->
                <el-form-item label="路灯时间段" prop="lampparagraph">
                    <div class="time-range-selector">
                        <el-time-picker v-model="startTime" placeholder="开始时间" format="HH:mm:ss" value-format="HH:mm:ss"
                            :clearable="false" @change="updateTimeRange" />
                        <span class="time-separator">至</span>
                        <el-time-picker v-model="endTime" placeholder="结束时间" format="HH:mm:ss" value-format="HH:mm:ss"
                            :clearable="false" @change="updateTimeRange" />
                    </div>
                    <div class="form-tip">
                        请选择路灯的开始和结束时间
                    </div>
                </el-form-item>

                <!-- 路灯内容 -->
                <el-form-item label="路灯内容" prop="lampcontent">
                    <el-input type="textarea" v-model="formData.lampcontent" placeholder="请输入路灯内容" :rows="4"
                        resize="none" class="content-textarea" />
                    <div class="form-tip">详细描述路灯内容，最多500字</div>
                </el-form-item>

                <el-form-item class="form-actions">
                    <el-button type="primary" @click="handleSubmit" :loading="submitting" class="submit-btn">
                        <el-icon>
                            <Upload />
                        </el-icon>
                        提交
                    </el-button>
                    <el-button @click="handleReset" class="reset-btn">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        重置
                    </el-button>
                </el-form-item>
            </el-form>
        </el-card>
    </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import {
    ElMessage, ElForm, ElFormItem, ElInput,
    ElButton, ElDatePicker, ElCard, ElDivider, ElTimePicker
} from 'element-plus';
import { Upload, Refresh } from '@element-plus/icons-vue';
import axios from 'axios';

// 表单数据
const formData = reactive({
    lamptime: '',
    lampcontent: '',
    lampparagraph: '',
    id:''
});

// 时间选择器数据
const startTime = ref('');
const endTime = ref('');

// 日期选择器配置
const pickerOptions = {
    disabledDate(time) {
        return time.getTime() > Date.now();
    }
};

// 提交状态
const submitting = ref(false);

// 更新时间段字符串
const updateTimeRange = () => {
    if (startTime.value && endTime.value) {
        // 格式化为 0:03:47-0:05:47 格式
        const formatTime = (timeStr) => {
            const [h, m, s] = timeStr.split(':');
            return `${h}:${m}:${s}`;
        };

        formData.lampparagraph = `${formatTime(startTime.value)}-${formatTime(endTime.value)}`;
    }
};

// 表单校验规则
const formRules = {
    lamptime: [
        { required: true, message: '请选择路灯直播日期', trigger: 'change' }
    ],
    lampcontent: [
        { required: true, message: '请输入路灯内容', trigger: 'blur' },
        { max: 500, message: '内容不能超过500字', trigger: 'blur' }
    ],
    lampparagraph: [
        { required: true, message: '请选择路灯时间段', trigger: 'change' },
        {
            validator: (rule, value, callback) => {
                if (!value) {
                    return callback(new Error('请选择时间段'));
                }

                // 验证开始时间早于结束时间
                const [start, end] = value.split('-');
                const startSec = convertToSeconds(start);
                const endSec = convertToSeconds(end);

                if (startSec >= endSec) {
                    return callback(new Error('开始时间必须早于结束时间'));
                }

                callback();
            },
            trigger: 'change'
        }
    ]
};

// 将时间字符串转换为秒数
const convertToSeconds = (timeStr) => {
    const [h, m, s] = timeStr.split(':').map(Number);
    return h * 3600 + m * 60 + s;
};

// 表单引用
const lampForm = ref(null);
// 生成唯一ID
const generateUniqueId = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `LD${timestamp}${random}`;
};
// 提交表单
const handleSubmit = () => {
    lampForm.value.validate((valid) => {
        if (valid) {
            submitting.value = true;
            formData.id = generateUniqueId();
            axios.post('/adminapi/lamp/add', formData)
                .then((response) => {
                    if (response.data.code === 200) {
                        ElMessage.success({
                            message: '提交成功',
                            duration: 2000,
                            showClose: true
                        });
                        handleReset();
                    } else {
                        ElMessage.error({
                            message: response.data.message || '提交失败',
                            duration: 3000,
                            showClose: true
                        });
                    }
                })
                .catch((error) => {
                    console.error(error);
                    ElMessage.error({
                        message: '网络异常，提交失败',
                        duration: 3000,
                        showClose: true
                    });
                })
                .finally(() => {
                    submitting.value = false;
                });
        }
    });
};

// 重置表单
const handleReset = () => {
    lampForm.value.resetFields();
    startTime.value = '';
    endTime.value = '';
};
</script>

<style scoped lang="scss">
.lamp-form-container {
    display: flex;
    justify-content: center;
    padding: 20px;
    background-color: #f5f7fa;
    min-height: 70%;
}

.form-card {
    width: 100%;
    max-width: 800px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    }
}

.form-header {
    text-align: center;
    padding: 0 20px;

    h2 {
        margin: 0;
        font-size: 1.5rem;
        color: #303133;
        font-weight: 600;
    }

    .el-divider {
        margin: 12px 0;
    }
}

.lamp-form {
    padding: 0 20px 20px;

    :deep(.el-form-item__label) {
        font-weight: 500;
        color: #606266;
        padding-bottom: 8px;
    }

    .date-picker {
        width: 100%;
    }

    .content-textarea {
        width: 100%;
    }

    .form-tip {
        font-size: 12px;
        color: #909399;
        margin-top: 6px;
        line-height: 1.4;
    }
}

.time-range-selector {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;

    :deep(.el-time-picker) {
        flex: 1;
    }

    .time-separator {
        color: #606266;
        font-size: 14px;
    }
}

.form-actions {
    display: flex;
    justify-content: center;
    margin-top: 30px;

    .submit-btn,
    .reset-btn {
        padding: 10px 24px;
        font-size: 14px;
        transition: all 0.3s ease;
    }

    .submit-btn {
        background-color: var(--el-color-primary);
        border-color: var(--el-color-primary);

        &:hover {
            opacity: 0.9;
            transform: translateY(-2px);
        }
    }

    .reset-btn {
        margin-left: 16px;

        &:hover {
            color: var(--el-color-primary);
            border-color: var(--el-color-primary);
            transform: translateY(-2px);
        }
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .lamp-form-container {
        padding: 10px;
    }

    .form-card {
        border-radius: 8px;
    }

    .lamp-form {
        padding: 0 10px 10px;
    }

    .time-range-selector {
        flex-direction: column;
        gap: 8px;

        .time-separator {
            display: none;
        }
    }

    .form-actions {
        flex-direction: column;
        gap: 12px;

        .reset-btn {
            margin-left: 0;
        }
    }
}
</style>